import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/contexts/WorkspaceContext";

export const useUnreadMessageCount = () => {
  const { workspace } = useWorkspace();
  const queryClient = useQueryClient();

  const { data: unreadCount = 0, isLoading } = useQuery({
    queryKey: ["unread-message-count", workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return 0;

      // Same rule as the Inbox (campaign conversations only unless the workspace shows private ones).
      const { data: cl } = await supabase.from("clients").select("id").eq("workspace_id", workspace.id).eq("status", "active");
      const ids = (cl ?? []).map((c: any) => c.id);
      if (!ids.length) return 0;
      const { data, error } = await supabase.rpc("inbox_unread_by_client" as any, { p_client_ids: ids });
      if (error) {
        console.error("Error fetching unread count:", error);
        return 0;
      }
      return ((data ?? []) as { unread: number }[]).reduce((s, r) => s + (r.unread || 0), 0);
    },
    enabled: !!workspace?.id,
    staleTime: Infinity, // Don't auto-refetch, rely on realtime
  });

  // Realtime subscription with a Postgres-side filter and a debounced
  // invalidation so a burst of incoming messages collapses into a single
  // refetch instead of one per row.
  useEffect(() => {
    if (!workspace?.id) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleInvalidate = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ["unread-message-count", workspace.id],
        });
      }, 500);
    };

    const channel = supabase
      .channel(`unread-count-updates-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'client_messages',
          filter: 'is_from_me=eq.false',
        },
        () => scheduleInvalidate()
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'client_messages',
        },
        (payload) => {
          // Invalidate when messages are marked as read
          if (payload.old?.is_read !== payload.new?.is_read) {
            scheduleInvalidate();
          }
        }
      )
      .subscribe();

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [workspace?.id, queryClient]);

  return { unreadCount, isLoading };
};
